import { Sha256 } from '@aws-crypto/sha256-js';
import { Buffer } from 'buffer';
import { inflate } from 'pako';
import { z } from 'zod';
export var Brand;
(function (Brand) {
    Brand["AUDI"] = "A";
    Brand["VW"] = "V";
    Brand["SKODA"] = "C";
    Brand["BENTLEY"] = "E";
    Brand["LAMBORGHINI"] = "L";
    Brand["MAN_TRUCKS_BUS"] = "M";
    Brand["VW_NUTZFAHRZEUGE"] = "N";
    Brand["PORSCHE"] = "P";
    Brand["SEAT"] = "S";
})(Brand || (Brand = {}));
export var Market;
(function (Market) {
    Market["ARE"] = "ARE";
    Market["ARG"] = "ARG";
    Market["AUS"] = "AUS";
    Market["AUT"] = "AUT";
    Market["BEL"] = "BEL";
    Market["BGR"] = "BGR";
    Market["BHR"] = "BHR";
    Market["BOL"] = "BOL";
    Market["BRA"] = "BRA";
    Market["CAN"] = "CAN";
    Market["CHE"] = "CHE";
    Market["CHL"] = "CHL";
    Market["CHN"] = "CHN";
    Market["COL"] = "COL";
    Market["CRI"] = "CRI";
    Market["CUW"] = "CUW";
    Market["CYM"] = "CYM";
    Market["CYP"] = "CYP";
    Market["DEU"] = "DEU";
    Market["DNK"] = "DNK";
    Market["DOM"] = "DOM";
    Market["ECU"] = "ECU";
    Market["EGY"] = "EGY";
    Market["ESP"] = "ESP";
    Market["EST"] = "EST";
    Market["FIN"] = "FIN";
    Market["FRA"] = "FRA";
    Market["GBR"] = "GBR";
    Market["GLP"] = "GLP";
    Market["GRC"] = "GRC";
    Market["GTM"] = "GTM";
    Market["GUF"] = "GUF";
    Market["HRV"] = "HRV";
    Market["HUN"] = "HUN";
    Market["IND"] = "IND";
    Market["IRL"] = "IRL";
    Market["ITA"] = "ITA";
    Market["JAM"] = "JAM";
    Market["JOR"] = "JOR";
    Market["JPN"] = "JPN";
    Market["KOR"] = "KOR";
    Market["KWT"] = "KWT";
    Market["LBN"] = "LBN";
    Market["LCA"] = "LCA";
    Market["LTU"] = "LTU";
    Market["LUX"] = "LUX";
    Market["LVA"] = "LVA";
    Market["MEX"] = "MEX";
    Market["MTQ"] = "MTQ";
    Market["NLD"] = "NLD";
    Market["NOR"] = "NOR";
    Market["NZL"] = "NZL";
    Market["OMN"] = "OMN";
    Market["PAN"] = "PAN";
    Market["PER"] = "PER";
    Market["POL"] = "POL";
    Market["PRT"] = "PRT";
    Market["PRY"] = "PRY";
    Market["QAT"] = "QAT";
    Market["ROU"] = "ROU";
    Market["RUS"] = "RUS";
    Market["SAU"] = "SAU";
    Market["SGP"] = "SGP";
    Market["SLV"] = "SLV";
    Market["SVK"] = "SVK";
    Market["SWE"] = "SWE";
    Market["SXM"] = "SXM";
    Market["THA"] = "THA";
    Market["TTO"] = "TTO";
    Market["TUR"] = "TUR";
    Market["TWN"] = "TWN";
    Market["URY"] = "URY";
    Market["USA"] = "USA";
    Market["VAT"] = "VAT";
    Market["VEN"] = "VEN";
    Market["VNM"] = "VNM";
    Market["ZAF"] = "ZAF";
})(Market || (Market = {}));
const MarketSchema = z.nativeEnum(Market);
const BrandSchema = z.nativeEnum(Brand);
const arraySchemaIgnoringInvalidElements = (elementSchema) => z.preprocess((array) => z
    .array(z.any())
    .parse(array)
    .filter((element) => elementSchema.safeParse(element).success), z.array(elementSchema));
const GrpContextSchema = z.object({
    identifier: z.string(),
    type: z.string(),
    roles: z.array(z.string()),
    market: MarketSchema,
    brand: BrandSchema
});
var CognitoAttribute;
(function (CognitoAttribute) {
    CognitoAttribute["subject"] = "sub";
    CognitoAttribute["userName"] = "cognito:username";
    CognitoAttribute["brands"] = "custom:brands";
    CognitoAttribute["grp"] = "custom:grp";
    CognitoAttribute["markets"] = "custom:markets";
    CognitoAttribute["groups"] = "cognito:groups";
})(CognitoAttribute || (CognitoAttribute = {}));
const numberToHex = (num) => num.toString(16).padStart(2, '0');
export class AvpContextClaims {
    static getUsername(claims) {
        return claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.userName];
    }
    static isGrp(claims) {
        const username = AvpContextClaims.getUsername(claims);
        if (username) {
            return username.includes('GroupRetailPortal') || username.includes('GRP');
        }
        return false;
    }
    static getMarkets(claims) {
        if (AvpContextClaims.isGrp(claims)) {
            return AvpContextClaims.getGrpMarkets(claims);
        }
        return AvpContextClaims.getCognitoMarkets(claims);
    }
    static getBrandMarketCombinations(claims) {
        if (AvpContextClaims.isGrp(claims)) {
            return AvpContextClaims.getGrpBrandMarketCombinations(claims);
        }
        return AvpContextClaims.getCognitoBrandMarketCombinations(claims);
    }
    static getGroups(claims) {
        const groupsClaim = claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.groups];
        if (typeof groupsClaim === 'string') {
            return groupsClaim.split(',');
        }
        return groupsClaim !== null && groupsClaim !== void 0 ? groupsClaim : [];
    }
    static isDealerOnly(claims) {
        var _a;
        const groups = this.getGroups(claims);
        return groups.length === 1 && ((_a = groups[0]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) === 'DEALER';
    }
    // returns (potentially empty) array for GRP users but undefined for Cognito users
    static getDealerIds(claims) {
        if (AvpContextClaims.isGrp(claims)) {
            return AvpContextClaims.getGrpDealerIds(claims);
        }
    }
    static getPrimaryBrand(claims) {
        const brands = AvpContextClaims.isGrp(claims)
            ? AvpContextClaims.getGrpBrands(claims)
            : AvpContextClaims.getCognitoBrands(claims);
        return brands === null || brands === void 0 ? void 0 : brands[0];
    }
    static getPrimaryMarket(claims) {
        var _a;
        return (_a = this.getMarkets(claims)) === null || _a === void 0 ? void 0 : _a[0];
    }
    static getPrimaryDealerId(claims) {
        var _a;
        return (_a = this.getDealerIds(claims)) === null || _a === void 0 ? void 0 : _a[0];
    }
    /** Returns a user identifier consisting of the hashed username 'salted' with the cognito unique user id from the 'sub'-claim. */
    static getUserPseudonym(claims) {
        const username = AvpContextClaims.getUsername(claims);
        const subject = claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.subject];
        if (!username || !subject) {
            throw new Error('Could not create pseudonym. Both cognito:userName and sub must be supplied.');
        }
        const hash = new Sha256();
        hash.update(username + subject);
        const hashArray = [...hash.digestSync()];
        return hashArray.map(numberToHex).join('');
    }
    static getCognitoBrands(claims) {
        const brandClaim = claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.brands];
        try {
            return arraySchemaIgnoringInvalidElements(BrandSchema).parse(brandClaim === null || brandClaim === void 0 ? void 0 : brandClaim.split(','));
        }
        catch (error) {
            return [];
        }
    }
    static getGrpBrands(claims) {
        const grpInfo = AvpContextClaims.getGrpContexts(claims);
        return grpInfo.map((context) => context.brand);
    }
    static getCognitoMarkets(claims) {
        const marketClaim = claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.markets];
        try {
            return arraySchemaIgnoringInvalidElements(MarketSchema).parse(JSON.parse(marketClaim !== null && marketClaim !== void 0 ? marketClaim : '[]'));
        }
        catch (error) {
            return [];
        }
    }
    static getGrpMarkets(claims) {
        const grpInfo = AvpContextClaims.getGrpContexts(claims);
        return grpInfo.map((context) => context.market);
    }
    static getCognitoBrandMarketCombinations(claims) {
        const markets = AvpContextClaims.getCognitoMarkets(claims);
        const brands = AvpContextClaims.getCognitoBrands(claims);
        return markets.flatMap((market) => brands.map((brand) => [brand, market]));
    }
    static getGrpBrandMarketCombinations(claims) {
        const grpInfo = AvpContextClaims.getGrpContexts(claims);
        return grpInfo.map((context) => [context.brand, context.market]);
    }
    static getGrpDealerIds(claims) {
        const grpInfo = AvpContextClaims.getGrpContexts(claims);
        return grpInfo.map((context) => context.identifier);
    }
    static getGrpContexts(claims) {
        const b64string = ((claims === null || claims === void 0 ? void 0 : claims[CognitoAttribute.grp]) || '').toString();
        if (!b64string) {
            return [];
        }
        const grpContextAsString = Buffer.from(inflate(new Uint8Array(Buffer.from(b64string, 'base64')))).toString('utf8');
        return z.array(GrpContextSchema).parse(JSON.parse(grpContextAsString));
    }
}
